import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FaMicrophone, FaPaperPlane, FaStop } from "react-icons/fa";
import "./IgniteChat.css";
import axios from "axios";

const Iva = () => {
  const [input, setInput] = useState("");
  const inputRef = useRef(null);
  const [isApiInProgress, setIsApiInProgress] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isRecording, setIsRecording] = useState(false);

  let recognition = useRef(null);
  const speechEndTimeout = useRef(null);
  const chatRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const handleMicClick = () => {
    if (isRecording) {
      recognition.current.stop();
      return;
    }

    recognition.current = new (
      window.SpeechRecognition || window.webkitSpeechRecognition
    )();
    recognition.current.continuous = false;
    recognition.current.interimResults = false;
    recognition.current.lang = "en-US";

    recognition.current.onstart = () => setIsRecording(true);
    recognition.current.onspeechend = () => {
      speechEndTimeout.current = setTimeout(() => {
        if (isRecording) recognition.current.stop();
      }, 5000);
    };
    recognition.current.onresult = (event) => {
      clearTimeout(speechEndTimeout.current);
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
      setIsRecording(false);
      if (inputRef.current) inputRef.current.focus();
    };
    recognition.current.onerror = () => {
      clearTimeout(speechEndTimeout.current);
      setIsRecording(false);
    };
    recognition.current.onend = () => {
      setIsRecording(false);
      if (inputRef.current) inputRef.current.focus();
    };
    recognition.current.start();
  };

  const handleSend = () => {
    if (!input.trim()) return;

    const messageText = input;
    setInput("");
    setMessages((prev) => [...prev, { text: messageText, isUser: true }]);
    setIsApiInProgress(true);

    axios({
      url: "https://wa278175geninaeuwa02-apim.azure-api.net/getAccentureContent",
      method: "post",
      data: {
        skill: "angular",
        prompt: messageText,
        user: localStorage.user.substring(0, localStorage.user.indexOf("@")),
      },
      headers: {
        "Content-Type": "application/json",
        Authorization: `${localStorage.token}`,
      },
    })
      .then((res) => {
        setMessages((prev) => [...prev, { text: res.data[0], isUser: false }]);
        setIsApiInProgress(false);
      })
      .catch(() => {
        setMessages((prev) => [
          ...prev,
          { text: "Some Error Occurred", isUser: false },
        ]);
        setIsApiInProgress(false);
      });
  };

  const SOURCE_URL = "https://geniustutor2.0.accenture.com/";

  function getInitials(name) {
    return name
      .split(",")
      .map((part) => part.trim()[0])
      .reverse()
      .join("")
      .toUpperCase();
  }

  return (
    <div className="ignite-container">
      <div className="ignite-page">
        {/* TITLE */}
        <div className="ignite-title-section">
          <h1 className="ignite-main-title">Welcome to Iva!</h1>
        </div>

        {/* HEADER */}
        <div className="ignite-header">
          <p className="ignite-eyebrow">
            Meet IVA — the Ignite Virtual Assistant.
          </p>
          <p className="ignite-tagline">
            A learning companion always within your reach.
          </p>
        </div>

        {/* LOGO */}
        <div className="ignite-logo-carousel">
          <div className="ignite-logo-slide">
            <img
              className="image-fluid"
              style={{ height: "10rem", width: "50rem" }}
              src="/Assets/askiva.png"
              alt="IVA Logo"
            />
          </div>
        </div>

        {/* CHAT WRAPPER */}
        <div className="ignite-chat-wrapper">
          <div className="ignite-chat" ref={chatRef}>
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`ignite-message ${msg.isUser ? "user" : "bot"}`}
              >
                {!msg.isUser && (
                  <div className="ignite-avatar">
                    <img src="./Assets/askivabot.png" alt="Bot" />
                  </div>
                )}

                <div className={`ignite-bubble ${msg.isUser ? "user" : "bot"}`}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {msg.text}
                  </ReactMarkdown>
                  {!msg.isUser && (
                    <div className="ignite-source">
                      <a
                        href={SOURCE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Source : Genius Tutor
                      </a>
                    </div>
                  )}
                </div>

                {msg.isUser && (
                  <div className="ignite-avatar user">
                    {getInitials(localStorage.name)}
                  </div>
                )}
              </div>
            ))}
            <div ref={chatEndRef} />

            {isApiInProgress && (
              <div className="ignite-message bot">
                <div className="ignite-avatar">
                  <img src="./Assets/askivabot.png" alt="Bot" />
                </div>
                <div className="ignite-bubble bot">
                  <span className="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* INPUT */}
          <div className="ignite-input-section">
            <div className={`ignite-input-box ${input ? "active" : ""}`}>
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                placeholder="Ask me anything..."
              />
              <div className="ignite-input-buttons">
                {!isRecording ? (
                  <FaMicrophone
                    className="ignite-input-icon mic"
                    onClick={handleMicClick}
                  />
                ) : (
                  <FaStop
                    className="ignite-input-icon mic recording"
                    onClick={() => recognition.current?.stop()}
                  />
                )}
                <FaPaperPlane
                  className="ignite-input-icon send"
                  onClick={handleSend}
                />
              </div>
            </div>
            <div className="ignite-footer">
              Response generated by AI. Verify accuracy and privacy restrictions
              before use.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Iva;
